import { Directive, EventEmitter, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Observable, combineLatest as observableCombineLatest } from "rxjs";
import { debounceTime, distinctUntilChanged, first, map, takeUntil, tap } from "rxjs/operators";

import { DEFAULT_PAGE_SIZE } from "./constants";
import { getFiltersFromRoute } from "./utils";

@Directive()
export abstract class BaseListingPageComponent implements OnInit, OnDestroy {
  protected abstract pageRoute: string;

  offset$ = new BehaviorSubject<number>(0);
  pageSize = DEFAULT_PAGE_SIZE;

  protected componentQueryParameters$: Observable<Array<Record<string, any>>>;

  constructor(protected router: Router, protected route: ActivatedRoute) {}

  protected abstract initComponentQueryParameters$(): void;
  protected abstract loadEntities(): void;
  protected abstract setComponentQueryParamsFromRouteParams$(): void;

  ngOnInit(): void {
    this.initComponentQueryParameters$();
    // this.setComponentQueryParamsFromRouteParams$();

    // this.setPageFromQueryParams();
    this.initComponentQueryParamsSubscription();
    this.initRouteQueryParamsSubscription();
  }

  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }
  protected onDestroyEmitter = new EventEmitter<void>();

  /**
   * By default, page cannot be < 0, and can increase without limit.
   * page$ observable is public and can be used to disable
   * next page / previous page buttons when appropriate
   */
  incrementOffset(val: number): void {
    this.offset$
      .pipe(
        first(),
        map((page) => +page + val),
        map((newPage) => (newPage >= 0 ? newPage : 0))
      )
      .subscribe((newPage) => this.offset$.next(newPage));
  }

  protected getFiltersFromRouteQueryParams(): Record<string, any> {
    return getFiltersFromRoute(this.route);
  }

  private initComponentQueryParamsSubscription(): void {
    observableCombineLatest([
      this.componentQueryParameters$.pipe(
        // changes in query params invalidate the previous cursor, so we reset it to 0
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        tap(() => this.offset$.next(0))
      ),
      this.offset$.pipe(
        // page$ can emit equal values in a series, don't handle those
        distinctUntilChanged(),

        // when page is zero, don't include in query params
        map((offset) => ({ offset: offset > 0 ? offset : undefined }))
      )
    ])
      .pipe(
        /**
         * componentQueryParameters$ has an immediate side effect of resetting page$ to 0.
         * Debounce one frame to ensure those two changes, and other changes in the same frame,
         * only trigger one emission.
         */
        debounceTime(0),

        /**
         * componentQueryParameters$ emits an array of the shape: [ { param1: val1 }, { param2: val2 } ]
         * This reduces those into a single object for the router.
         */
        map(
          ([filters, page]: [
            Array<Record<string, any>>,
            { offset: number }
          ]) => {
            return filters.reduce(
              (acc, filter) => {
                return { ...acc, ...filter };
              },
              { ...page }
            );
          }
        ),

        // unsubscribe onDestroy
        takeUntil(this.onDestroyEmitter)
      )
      .subscribe((queryParams) => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams,
          replaceUrl: true,
          queryParamsHandling: "merge"
        });
      });
  }

  private setPageFromRouteQueryParams(): void {
    const { offset } = this.route.snapshot.queryParams;

    this.offset$.pipe(first()).subscribe((currentOffset) => {
      // cast to number from queryParams string
      const newOffset = offset != null ? +offset : 0;

      if (newOffset !== currentOffset) {
        this.offset$.next(newOffset);
      }
    });
  }

  /**
   * Note: It's important that setComponentQueryParamsFromRouteParams
   * is called before setPageFromRouteQueryParams. Changes in componentQueryParams
   * (other than page / offset) reset page to 0. So we need to handle to changes
   * to all other query params before handling changes to page / offset, so
   * that changes to other params don't override changes to page.
   */
  private initRouteQueryParamsSubscription(): void {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(() => {
        this.setComponentQueryParamsFromRouteParams$();
        this.setPageFromRouteQueryParams();
        this.loadEntities();
      });
  }
}
